import { useEffect, useReducer } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import {
  EventBus,
  GameEvents,
  type LeagueSeasonEndedPayload,
  type TournamentWonPayload,
  type BoardObjectivePayload,
  type ClubChangedPayload,
} from '@/game/utils/EventBus'
import type { Achievement } from '@/config/achievementsConfig'
import { celebrationQueueReducer, initialQueueState } from './celebrationQueue'
import { isBigMoment } from './celebrationTypes'
import { AchievementBanner } from './AchievementBanner'
import { ObjectiveBanner } from './ObjectiveBanner'
import { MomentOverlay } from './MomentOverlay'

const ACHIEVEMENT_MS = 3500
const BIG_MOMENT_MS = 8000

// Single celebration entry point: queues achievement unlocks and
// competition milestones and presents them one at a time. Purely visual —
// rewards are applied where the events are emitted.
export function CelebrationManager() {
  const [state, dispatch] = useReducer(celebrationQueueReducer, initialQueueState)
  const navigate = useNavigate()

  useEffect(() => {
    const onAchievement = (achievement: Achievement) => {
      dispatch({ type: 'enqueue', celebration: { kind: 'achievement', achievement } })
    }
    const onSeasonEnded = (payload: LeagueSeasonEndedPayload) => {
      if (payload.title) {
        dispatch({
          type: 'enqueue',
          celebration: { kind: 'title', divisionName: payload.divisionName, season: payload.season },
        })
      } else if (payload.promoted) {
        dispatch({
          type: 'enqueue',
          celebration: { kind: 'promotion', divisionName: payload.divisionName, position: payload.position },
        })
      }
    }
    const onTournamentWon = (payload: TournamentWonPayload) => {
      dispatch({
        type: 'enqueue',
        celebration: { kind: 'tournamentWon', tournamentName: payload.tournamentName, prize: payload.prize },
      })
    }
    const onObjectiveMet = (p: BoardObjectivePayload) => {
      dispatch({ type: 'enqueue', celebration: { kind: 'objectiveMet', label: p.objectiveLabel, divisionName: p.divisionName } })
    }
    const onObjectiveFailed = (p: BoardObjectivePayload) => {
      dispatch({ type: 'enqueue', celebration: { kind: 'objectiveFailed', label: p.objectiveLabel } })
    }
    // 'fired' is NOT celebrated: the GameOverScreen "Destituido" covers that
    // moment and avoids stacking two full-screen overlays.
    const onClubChanged = (p: ClubChangedPayload) => {
      dispatch({ type: 'enqueue', celebration: { kind: 'clubChanged', clubName: p.clubName, divisionName: p.divisionName } })
    }

    EventBus.on(GameEvents.ACHIEVEMENT_UNLOCKED, onAchievement)
    EventBus.on(GameEvents.LEAGUE_SEASON_ENDED, onSeasonEnded)
    EventBus.on(GameEvents.TOURNAMENT_WON, onTournamentWon)
    EventBus.on(GameEvents.BOARD_OBJECTIVE_MET, onObjectiveMet)
    EventBus.on(GameEvents.BOARD_OBJECTIVE_FAILED, onObjectiveFailed)
    EventBus.on(GameEvents.CLUB_CHANGED, onClubChanged)
    return () => {
      EventBus.off(GameEvents.ACHIEVEMENT_UNLOCKED, onAchievement)
      EventBus.off(GameEvents.LEAGUE_SEASON_ENDED, onSeasonEnded)
      EventBus.off(GameEvents.TOURNAMENT_WON, onTournamentWon)
      EventBus.off(GameEvents.BOARD_OBJECTIVE_MET, onObjectiveMet)
      EventBus.off(GameEvents.BOARD_OBJECTIVE_FAILED, onObjectiveFailed)
      EventBus.off(GameEvents.CLUB_CHANGED, onClubChanged)
    }
  }, [])

  // Auto-advance: banners are short; big moments also expire so an
  // unattended queue never stalls.
  useEffect(() => {
    if (!state.current) return
    const ms = isBigMoment(state.current) ? BIG_MOMENT_MS : ACHIEVEMENT_MS
    const timer = setTimeout(() => dispatch({ type: 'advance' }), ms)
    return () => clearTimeout(timer)
  }, [state.current])

  const current = state.current

  return (
    <>
      <div className="celebration-layer">
        <AnimatePresence>
          {current?.kind === 'achievement' && (
            <AchievementBanner
              key={current.achievement.id}
              achievement={current.achievement}
              onOpen={() => {
                navigate('/career?tab=logros')
                dispatch({ type: 'advance' })
              }}
            />
          )}
          {current?.kind === 'objectiveMet' && (
            <ObjectiveBanner key="obj-met" met label={current.label} divisionName={current.divisionName} />
          )}
          {current?.kind === 'objectiveFailed' && (
            <ObjectiveBanner key="obj-failed" met={false} label={current.label} />
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {current && isBigMoment(current) && (
          <MomentOverlay
            celebration={current}
            onDismiss={() => dispatch({ type: 'advance' })}
          />
        )}
      </AnimatePresence>
    </>
  )
}
